import React, { useState } from 'react'
import { ISelecetedFilters, ISummaryDatum, ISummaryData } from '../../utility/types'
import { styled, Flex, Span } from '@fareye/ui'
import { IKeyValue, IPageRenderConfig } from '@fareye/types'
import { inTransitConstant } from "../../../../config/constants"
import SummaryCard from './summaryComponents/SummaryCard'
import { camelCase, cloneDeep } from '@fareye/utils'

interface ISummaryList {
  summaryData: ISummaryData,
  selectedFilters: ISelecetedFilters,
  upsertSelectedFilters: (filterType: string, filterKey: string, filterValue: any) => void,
  config: IPageRenderConfig
}

interface ISummary {
  summaryKey: string
  summaryDatum: ISummaryDatum
  selectedFilters: ISelecetedFilters,
  upsertSelectedFilters: (filterType: string, filterKey: string, filterValue: any) => void
  configData: IPageRenderConfig
}

type ISelectedSummaryFilter = Array<string>

const SummaryListContainer = styled(Flex)`
  flex-direction: column;
  gap: 15px;
  height: 100%;

  @media (min-width: 600px) {
    justify-content: space-between;
  }
`

const SummaryWrapper = styled(Flex)`
  flex-direction: column;
  gap: 12px;
  ${(props: any) => props?.styling?.style}
`
const SummaryCountList = styled(Flex)`
  flex-wrap: wrap;
  ${(props: any) => props?.styling?.style}
`

const Summary: React.FC<ISummary> = (props) => {
  const { summaryKey, configData, summaryDatum, selectedFilters, upsertSelectedFilters } = props
  const { config, children, styling } = configData as IPageRenderConfig
  const {type, title} = config as any
  const summaryLabel = title ?? inTransitConstant[camelCase(summaryKey)]
  const filterType: string = type ?? "singleSelect"
  const { aggregations = [] } = summaryDatum
  const [selectedSummaryFilter, setSelectedSummaryFilter] = useState<ISelectedSummaryFilter>(selectedFilters[filterType]?.[summaryKey] || [])
  const handleChange = (value: string) => {
    const filterValue = cloneDeep(selectedSummaryFilter)
    const updatedFilterValue = type === "multiSelect"
    ? filterValue.includes(value)
      ? filterValue.filter((el: string) => el !== value) // Remove if already selected
      : [...filterValue, value] // Add if not selected
    : [value] // Default case for single selection

    // Update the state and external filter function
    setSelectedSummaryFilter(updatedFilterValue)
    upsertSelectedFilters(filterType, summaryKey, updatedFilterValue)
  }
  const { summaryCountList: summaryCountListConfig} = children as IKeyValue<IPageRenderConfig>
  const { component: summaryCardComponent, config: summaryCardConfig } = summaryCountListConfig
  const { cardSettings, labelLookup } = summaryCardConfig as any
  const totalCount = summaryCountListConfig.config.showAllCountCard
    ? aggregations?.reduce((a: number, b: any) => a + b.count, 0)
    : 0
  return (
    <SummaryWrapper {...configData}>
      <Span variant='h5' style={{ fontWeight: 600, fontFamily: "lato" }}>
        {summaryLabel}
      </Span>
      <SummaryCountList {...summaryCountListConfig}>
      {summaryCountListConfig.config.showAllCountCard && (
        <SummaryCard
          key={`${summaryKey}-All`}
          component={summaryCardComponent}
          isDisabled={!!cardSettings?.all?.isDisabled}
          handleClick={() => {
            setSelectedSummaryFilter([]);
              upsertSelectedFilters(filterType, summaryKey, [])
          }}
          label={labelLookup["all"] || `All ${summaryLabel}`}
          count={totalCount}
          isSelected={selectedSummaryFilter.length === 0}
        />
      )}
      {aggregations.map(({ label, value, count, prettyName, key }) => {
        const isHidden = !!cardSettings?.[value]?.isHidden;
        const isDisabled = !!cardSettings?.[value]?.isDisabled;

        return (
          !isHidden && (
            <SummaryCard
              key={`${summaryKey}-${value}`}
              component={summaryCardComponent}
              isDisabled={isDisabled}
              handleClick={() => handleChange(value)}
              label={labelLookup[value] || label}
              count={count}
              isSelected={selectedSummaryFilter.includes(value)}
            />
          )
        )
      })}
      </SummaryCountList>
    </SummaryWrapper>
  )
}

const SummaryList: React.FC<ISummaryList> = (props) => {
  const { config } = props
  const { summaryData, selectedFilters, upsertSelectedFilters } = props
  return (
    <SummaryListContainer>
      {
        Object.entries(config).map(([key, configData]) => {
          return (
            <Summary
              key={key}
              summaryKey={key}
              summaryDatum={summaryData?.[key] || {}}
              selectedFilters={selectedFilters}
              upsertSelectedFilters={upsertSelectedFilters}
              configData={configData}
            />
          )
        })
      }
    </SummaryListContainer>
  )
}

export default SummaryList